import { MASTRA_FRAMEWORK_PUBLIC_KEY, MASTRA_FRAMEWORK_PUBLIC_KEY as MASTRA_FRAMEWORK_PUBLIC_KEY$1, MastraServer as MastraServer$1, applyMcpRequestAuth, checkRouteFGA, getCustomHTTPExceptionResponse, isZodError, normalizeQueryParams, redactStreamChunk, serializeStreamChunk } from "@mastra/server/server-adapter";
import { toFetchResponse, toReqRes } from "fetch-to-node";
import { bodyLimit } from "hono/body-limit";
import { stream } from "hono/streaming";
import { RequestContext } from "@mastra/core/request-context";
import { coreAuthMiddleware } from "@mastra/server/auth";
import { ViewerRegistry, handleInputMessage } from "@mastra/server/browser-stream";
//#region src/mcp-disconnect.ts
/**
* Propagates a client disconnect to the simulated Node response produced by `toReqRes`.
*
* `fetch-to-node` builds the outgoing body from `res` events but never observes cancellation
* of the stream it hands back. When an MCP Streamable HTTP client drops its session, nothing
* tells `res` that the socket is gone, so the MCP transport keeps its SSE keep-alive timer
* armed. The next keep-alive tick writes into an already-closed stream controller, and because
* that write originates in a timer callback the resulting `ERR_INVALID_STATE` is unhandled and
* takes down the process.
*
* Emitting `close` on `res` is the signal the MCP Node transport listens for: it aborts the
* request's AbortController, which breaks the write loop and tears down the SSE stream,
* clearing the keep-alive timer. No post-disconnect write is ever attempted.
*/
function propagateClientDisconnect(fetchResponse, res) {
	const upstream = fetchResponse.body;
	if (!upstream) return fetchResponse;
	let disconnected = false;
	const disconnect = () => {
		if (disconnected) return;
		disconnected = true;
		try {
			res.emit("close");
		} catch {}
		reader.read().then(function drain({ done }) {
			return done ? void 0 : reader.read().then(drain);
		}, () => {});
	};
	const reader = upstream.getReader();
	const body = new ReadableStream({
		async pull(controller) {
			const { done, value } = await reader.read();
			if (done) {
				controller.close();
				return;
			}
			controller.enqueue(value);
		},
		cancel() {
			disconnect();
		}
	});
	return new Response(body, {
		status: fetchResponse.status,
		statusText: fetchResponse.statusText,
		headers: fetchResponse.headers
	});
}
//#endregion
//#region src/auth-middleware.ts
function createAuthMiddleware({ mastra, requiresAuth = true }) {
	return async (c, next) => {
		if (!requiresAuth) return next();
		const authConfig = mastra.getServer()?.auth;
		if (!authConfig) return next();
		const requestContext = c.get("requestContext") ?? new RequestContext();
		c.set("requestContext", requestContext);
		c.set("mastra", c.get("mastra") ?? mastra);
		const path = c.req.path;
		const method = c.req.method;
		const customRouteAuthConfig = new Map(c.get("customRouteAuthConfig") ?? []);
		customRouteAuthConfig.set(`${method}:${path}`, true);
		const authHeader = c.req.header("Authorization");
		let token = authHeader ? authHeader.replace("Bearer ", "") : null;
		if (!token) token = c.req.query("apiKey") || null;
		const result = await coreAuthMiddleware({
			path,
			method,
			getHeader: (name) => c.req.header(name),
			mastra,
			authConfig,
			customRouteAuthConfig,
			requestContext,
			rawRequest: c.req.raw,
			token,
			buildAuthorizeContext: () => c
		});
		if (result.action === "next") return next();
		return c.json(result.body, result.status);
	};
}
//#endregion
//#region src/browser-stream/index.ts
/**
* Set up WebSocket-based browser stream endpoint for real-time screencast viewing.
*
* Creates a WebSocket route at `/browser/:agentId/stream` that:
* - Accepts viewer connections
* - Starts screencast when first viewer connects
* - Broadcasts frames to all connected viewers
* - Stops screencast when last viewer disconnects
*
* **Note**: Requires `ws` package to be installed. If not available, returns null
* and logs a warning. Browser streaming will be disabled but everything else works.
*
* @param app - The Hono application instance
* @param config - Configuration for browser stream
* @returns Object containing injectWebSocket function and registry instance, or null if ws is not available
*
* @example
* ```typescript
* import { Hono } from 'hono';
* import { serve } from '@hono/node-server';
* import { setupBrowserStream } from '@mastra/hono';
*
* const app = new Hono();
* const browserStream = await setupBrowserStream(app, {
*   getToolset: (agentId) => browserToolsets.get(agentId),
* });
*
* const server = serve({ fetch: app.fetch, port: 4111 });
* browserStream?.injectWebSocket(server);
* ```
*/
async function setupBrowserStream(app, config) {
	let createNodeWebSocket;
	try {
		createNodeWebSocket = (await import(
			/* @vite-ignore */
			/* webpackIgnore: true */
			"@hono/node-ws"
)).createNodeWebSocket;
	} catch {
		return null;
	}
	const { injectWebSocket, upgradeWebSocket } = createNodeWebSocket({ app });
	const registry = new ViewerRegistry();
	const rawPrefix = config.apiPrefix ?? "/api";
	const apiPrefix = (rawPrefix.endsWith("/") ? rawPrefix.slice(0, -1) : rawPrefix) || "/api";
	app.get("/browser/:agentId/stream", upgradeWebSocket((c) => {
		const agentId = c.req.param("agentId");
		const threadId = c.req.query("threadId");
		const viewerKey = threadId ? `${agentId}:${threadId}` : agentId;
		return {
			onOpen(_event, ws) {
				ws.send(JSON.stringify({ status: "connected" }));
				registry.addViewer(viewerKey, ws, config.getToolset, agentId, threadId);
			},
			onMessage(event, _ws) {
				const data = typeof event.data === "string" ? event.data : null;
				if (data) handleInputMessage(data, config.getToolset, agentId, threadId);
			},
			onClose(_event, ws) {
				registry.removeViewer(viewerKey, ws);
			},
			onError(event, ws) {
				console.error("[BrowserStream] WebSocket error:", event);
				registry.removeViewer(viewerKey, ws);
			}
		};
	}));
	app.get(`${apiPrefix}/agents/:agentId/browser/session`, async (c) => {
		const agentId = c.req.param("agentId");
		if (!agentId) return c.json({ error: "Agent ID is required" }, 400);
		const threadId = c.req.query("threadId");
		const toolset = await config.getToolset(agentId);
		if (!toolset) return c.json({
			hasSession: false,
			screencastAvailable: true
		});
		const hasSession = threadId ? toolset.hasThreadSession(threadId) : false;
		return c.json({
			hasSession,
			screencastAvailable: true
		});
	});
	app.post(`${apiPrefix}/agents/:agentId/browser/close`, async (c) => {
		const agentId = c.req.param("agentId");
		if (!agentId) return c.json({ error: "Agent ID is required" }, 400);
		const toolset = await config.getToolset(agentId);
		if (!toolset) return c.json({ error: "No browser session for this agent" }, 404);
		try {
			let threadId;
			try {
				threadId = (await c.req.json())?.threadId;
			} catch {}
			const scope = toolset.getScope();
			const viewerKey = threadId ? `${agentId}:${threadId}` : agentId;
			if (scope === "thread" && threadId) {
				await registry.closeBrowserSession(viewerKey);
				if ("closeThreadSession" in toolset && typeof toolset.closeThreadSession === "function") await toolset.closeThreadSession(threadId);
			} else {
				await registry.closeBrowserSession(viewerKey);
				await toolset.close();
			}
			return c.json({ success: true });
		} catch (error) {
			console.error(`[BrowserStream] Error closing browser for ${agentId}:`, error);
			return c.json({ error: "Failed to close browser" }, 500);
		}
	});
	return {
		injectWebSocket,
		registry
	};
}
//#endregion
//#region src/index.ts
let _hasPermissionPromise;
function loadHasPermission() {
	if (!_hasPermissionPromise) _hasPermissionPromise = import("@mastra/core/auth/ee").then((m) => m.hasPermission).catch(() => {
		console.error("[@mastra/hono] Auth features require @mastra/core >= 1.6.0. Please upgrade: npm install @mastra/core@latest");
	});
	return _hasPermissionPromise;
}
/**
* Wrap a Hono middleware handler so it becomes a no-op for framework-public
* routes (routes registered with `requiresAuth: false`).
*
* Adapters that expose user-provided middleware — for example `serverMiddleware`
* on the Mastra instance or `server.middleware` in Mastra config — MUST wrap
* those handlers with this before registering them. This is the framework's
* guarantee that user middleware cannot accidentally (or intentionally) 401
* routes the framework needs to keep reachable (e.g. Studio sign-in endpoints).
*
* The framework-public flag is computed once per request by
* {@link MastraServer.registerContextMiddleware} and stashed on the Hono
* context under `MASTRA_FRAMEWORK_PUBLIC_KEY`.
*/
const skipIfFrameworkPublic = (handler) => {
	return async (c, next) => {
		if (c.get(MASTRA_FRAMEWORK_PUBLIC_KEY$1)) return next();
		return handler(c, next);
	};
};
/**
* Context key holding a pristine clone of the incoming request, captured by
* the context middleware before user middleware runs. The custom-route bridge
* reads the body from this clone so user middleware that consumes the request
* body (e.g. `await c.req.json()`) does not break custom API routes.
*/
const MASTRA_PRISTINE_REQUEST_KEY = "__mastraPristineRequest";
const BODY_METHODS = /* @__PURE__ */ new Set([
	"POST",
	"PUT",
	"PATCH",
	"DELETE"
]);
var MastraServer = class extends MastraServer$1 {
	createContextMiddleware() {
		return async (c, next) => {
			if (this.hasCustomRouteHandler && BODY_METHODS.has(c.req.method) && c.req.raw.body) c.set(MASTRA_PRISTINE_REQUEST_KEY, c.req.raw.clone());
			const originalJson = c.req.json.bind(c.req);
			let jsonPromise;
			c.req.json = () => {
				if (!jsonPromise) jsonPromise = originalJson().then((body) => {
					c.set("cachedBody", body);
					return body;
				});
				return jsonPromise;
			};
			let bodyRequestContext;
			let paramsRequestContext;
			if (c.req.method === "POST" || c.req.method === "PUT") {
				const contentType = c.req.header("content-type");
				const contentLength = c.req.header("content-length");
				if (contentType?.includes("application/json") && contentLength !== "0") try {
					const body = await c.req.raw.clone().json();
					if (body.requestContext) bodyRequestContext = body.requestContext;
				} catch {}
			}
			if (c.req.method === "GET") try {
				const encodedRequestContext = c.req.query("requestContext");
				if (encodedRequestContext) try {
					paramsRequestContext = JSON.parse(encodedRequestContext);
				} catch {
					try {
						const json = Buffer.from(encodedRequestContext, "base64").toString("utf-8");
						paramsRequestContext = JSON.parse(json);
					} catch {}
				}
			} catch {}
			const requestContext = this.mergeRequestContext({
				paramsRequestContext,
				bodyRequestContext
			});
			this.applyRequestMetadataToContext({
				requestContext,
				getHeader: (name) => c.req.header(name)
			});
			c.set("requestContext", requestContext);
			c.set("mastra", this.mastra);
			c.set("registeredTools", this.tools || {});
			c.set("taskStore", this.taskStore);
			c.set("abortSignal", c.req.raw.signal);
			c.set("customRouteAuthConfig", this.customRouteAuthConfig);
			return next();
		};
	}
	async stream(route, res, result) {
		const streamFormat = route.streamFormat || "stream";
		if (streamFormat === "sse") {
			res.header("Content-Type", "text/event-stream");
			res.header("Cache-Control", "no-cache");
			res.header("Connection", "keep-alive");
			res.header("X-Accel-Buffering", "no");
		} else res.header("Content-Type", "text/plain");
		res.header("Transfer-Encoding", "chunked");
		return stream(res, async (stream) => {
			if (streamFormat === "sse" && route.sseFlushOnConnect) await stream.write(": connected\n\n");
			const reader = (result instanceof ReadableStream ? result : result.fullStream).getReader();
			stream.onAbort(() => {
				reader.cancel("request aborted").catch(() => {});
			});
			try {
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					if (value) {
						if (streamFormat === "sse" && typeof value === "string" && value.startsWith(":")) {
							await stream.write(value);
							continue;
						}
						const outputValue = this.streamOptions?.redact ?? true ? redactStreamChunk(value) : value;
						const serialized = serializeStreamChunk(outputValue);
						if (!serialized.ok) {
							this.mastra.getLogger()?.error("Failed to serialize stream chunk, skipping", {
								path: route.path,
								chunkType: outputValue?.type,
								error: serialized.error.message
							});
							continue;
						}
						if (streamFormat === "sse") await stream.write(`data: ${serialized.json}\n\n`);
						else await stream.write(serialized.json + "");
					}
				}
				if (streamFormat === "sse") await stream.write("data: [DONE]\n\n");
			} catch (error) {
				this.mastra.getLogger()?.error("Error in stream processing", { error: error instanceof Error ? {
					message: error.message,
					stack: error.stack
				} : error });
			} finally {
				await stream.close();
			}
		}, async (err) => {
			this.mastra.getLogger()?.error("Stream error callback", { error: err instanceof Error ? {
				message: err.message,
				stack: err.stack
			} : err });
		});
	}
	async getParams(route, request) {
		const urlParams = request.param();
		const queryParams = normalizeQueryParams(request.queries());
		let body;
		let bodyParseError;
		if (route.method === "POST" || route.method === "PUT" || route.method === "PATCH" || route.method === "DELETE") {
			const contentType = request.header("content-type") || "";
			if (contentType.includes("multipart/form-data")) try {
				const formData = await request.formData();
				body = await this.parseFormData(formData);
			} catch (error) {
				this.mastra.getLogger()?.error("Failed to parse multipart form data", { error: error instanceof Error ? {
					message: error.message,
					stack: error.stack
				} : error });
				if (error instanceof Error && error.message.toLowerCase().includes("size")) throw error;
				bodyParseError = { message: error instanceof Error ? error.message : "Failed to parse multipart form data" };
			}
			else if (contentType.includes("application/json")) {
				const bodyText = await request.raw.clone().text();
				if (bodyText && bodyText.trim().length > 0) try {
					body = JSON.parse(bodyText);
				} catch (error) {
					this.mastra.getLogger()?.error("Failed to parse JSON body", { error: error instanceof Error ? {
						message: error.message,
						stack: error.stack
					} : error });
					bodyParseError = { message: error instanceof Error ? error.message : "Invalid JSON in request body" };
				}
			}
		}
		return {
			urlParams,
			queryParams,
			body,
			bodyParseError
		};
	}
	/**
	* Parse FormData into a plain object, converting File objects to Buffers.
	*/
	async parseFormData(formData) {
		const result = {};
		for (const [key, value] of formData.entries()) if (value instanceof File) {
			const arrayBuffer = await value.arrayBuffer();
			result[key] = Buffer.from(arrayBuffer);
		} else if (typeof value === "string") try {
			result[key] = JSON.parse(value);
		} catch {
			result[key] = value;
		}
		else result[key] = value;
		return result;
	}
	async sendResponse(route, response, result, prefix) {
		const resolvedPrefix = prefix ?? this.prefix ?? "";
		if (result && typeof result === "object" && "__refreshHeaders" in result) {
			const refreshHeaders = result.__refreshHeaders;
			for (const [key, value] of Object.entries(refreshHeaders)) response.header(key, value);
			delete result.__refreshHeaders;
		}
		if (route.responseType === "json") return response.json(result, 200);
		else if (route.responseType === "stream") return this.stream(route, response, result);
		else if (route.responseType === "datastream-response") return result;
		else if (route.responseType === "mcp-http") {
			const { server, httpPath, mcpOptions: routeMcpOptions } = result;
			const { req, res } = toReqRes(response.req.raw);
			const { setRequestAuth, ...options } = {
				...this.mcpOptions,
				...routeMcpOptions
			};
			await applyMcpRequestAuth({
				req,
				requestContext: response.get("requestContext"),
				setRequestAuth
			});
			server.startHTTP({
				url: new URL(response.req.url),
				httpPath: `${resolvedPrefix}${httpPath}`,
				req,
				res,
				options: Object.keys(options).length > 0 ? options : void 0
			}).catch((e) => {
				this.mastra.getLogger()?.error("[MCP HTTP] Error in background startHTTP:", { error: e instanceof Error ? {
					message: e.message,
					stack: e.stack
				} : e });
				try {
					if (!res.headersSent) {
						res.writeHead(500, { "Content-Type": "application/json" });
						res.end(JSON.stringify({
							jsonrpc: "2.0",
							error: {
								code: -32603,
								message: "Internal server error"
							},
							id: null
						}));
					}
				} catch {}
			});
			return propagateClientDisconnect(await toFetchResponse(res), res);
		} else if (route.responseType === "mcp-sse") {
			const { server, ssePath, messagePath } = result;
			try {
				const { req } = toReqRes(response.req.raw);
				await applyMcpRequestAuth({
					req,
					requestContext: response.get("requestContext"),
					setRequestAuth: this.mcpOptions?.setRequestAuth
				});
				return await server.startHonoSSE({
					url: new URL(response.req.url),
					ssePath: `${resolvedPrefix}${ssePath}`,
					messagePath: `${resolvedPrefix}${messagePath}`,
					context: response,
					authInfo: req.auth
				});
			} catch {
				return response.json({ error: "Error handling MCP SSE request" }, 500);
			}
		} else return response.status(500);
	}
	async registerRoute(app, route, { prefix: prefixParam } = {}) {
		const prefix = prefixParam ?? this.prefix ?? "";
		const maxSize = route.maxBodySize ?? this.bodyLimitOptions?.maxSize;
		const isBodyMethod = [
			"POST",
			"PUT",
			"PATCH",
			"DELETE"
		].includes(route.method.toUpperCase());
		const middlewares = [];
		if (isBodyMethod && maxSize !== void 0) middlewares.push(bodyLimit({
			maxSize,
			onError: (c) => {
				let errorResponse = { error: "Request body too large" };
				if (route.maxBodySize === void 0 && this.bodyLimitOptions) try {
					errorResponse = this.bodyLimitOptions.onError(errorResponse);
				} catch {}
				return c.json(errorResponse, 413);
			}
		}));
		app[route.method.toLowerCase()](`${prefix}${route.path}`, ...middlewares, async (c) => {
			const authResult = await this.checkRouteAuth(route, {
				path: c.req.path,
				method: c.req.method,
				getHeader: (name) => c.req.header(name),
				getQuery: (name) => c.req.query(name),
				requestContext: c.get("requestContext"),
				request: c.req.raw,
				buildAuthorizeContext: () => c
			});
			if (authResult) {
				if (authResult.headers) for (const [key, value] of Object.entries(authResult.headers)) c.header(key, value);
				if (authResult.error) return c.json({ error: authResult.error }, authResult.status);
			}
			const params = await this.getParams(route, c.req);
			if (params.bodyParseError) return c.json({
				error: "Invalid request body",
				issues: [{
					field: "body",
					message: params.bodyParseError.message
				}]
			}, 400);
			if (params.queryParams) try {
				params.queryParams = await this.parseQueryParams(route, params.queryParams);
			} catch (error) {
				this.mastra.getLogger()?.error("Error parsing query params", { error: error instanceof Error ? {
					message: error.message,
					stack: error.stack
				} : error });
				if (isZodError(error)) {
					const { status, body } = this.resolveValidationError(route, error, "query");
					return c.json(body, status);
				}
				return c.json({
					error: "Invalid query parameters",
					issues: [{
						field: "unknown",
						message: error instanceof Error ? error.message : "Unknown error"
					}]
				}, 400);
			}
			if (params.body !== void 0 || route.bodySchema) try {
				params.body = await this.parseBody(route, params.body);
			} catch (error) {
				this.mastra.getLogger()?.error("Error parsing body", { error: error instanceof Error ? {
					message: error.message,
					stack: error.stack
				} : error });
				if (isZodError(error)) {
					const { status, body } = this.resolveValidationError(route, error, "body");
					return c.json(body, status);
				}
				return c.json({
					error: "Invalid request body",
					issues: [{
						field: "unknown",
						message: error instanceof Error ? error.message : "Unknown error"
					}]
				}, 400);
			}
			if (params.urlParams) try {
				params.urlParams = await this.parsePathParams(route, params.urlParams);
			} catch (error) {
				this.mastra.getLogger()?.error("Error parsing path params", { error: error instanceof Error ? {
					message: error.message,
					stack: error.stack
				} : error });
				if (isZodError(error)) {
					const { status, body } = this.resolveValidationError(route, error, "path");
					return c.json(body, status);
				}
				return c.json({
					error: "Invalid path parameters",
					issues: [{
						field: "unknown",
						message: error instanceof Error ? error.message : "Unknown error"
					}]
				}, 400);
			}
			const handlerParams = {
				...params.urlParams,
				...params.queryParams,
				...typeof params.body === "object" ? params.body : {},
				requestContext: c.get("requestContext"),
				mastra: this.mastra,
				registeredTools: c.get("registeredTools"),
				taskStore: c.get("taskStore"),
				abortSignal: c.get("abortSignal"),
				routePrefix: prefix,
				request: c.req.raw
			};
			const requestContext = c.get("requestContext");
			if (this.mastra.getStudio?.()?.auth || this.mastra.getServer()?.auth) {
				const hasPermission = await loadHasPermission();
				if (hasPermission) {
					const userPermissions = requestContext.get("mastra__userPermissions");
					const permissionError = this.checkRoutePermission(route, userPermissions, hasPermission, requestContext);
					if (permissionError) return c.json({
						error: permissionError.error,
						message: permissionError.message
					}, permissionError.status);
				}
			}
			const fgaError = await checkRouteFGA(this.mastra, route, c.get("requestContext"), {
				...params.urlParams,
				...params.queryParams,
				...typeof params.body === "object" ? params.body : {}
			});
			if (fgaError) return c.json({
				error: fgaError.error,
				message: fgaError.message
			}, fgaError.status);
			try {
				const result = await route.handler(handlerParams);
				return this.sendResponse(route, c, result, prefix);
			} catch (error) {
				const httpStatus = error && typeof error === "object" && "status" in error ? error.status : void 0;
				if (!(typeof httpStatus === "number" && httpStatus >= 400 && httpStatus < 500)) this.mastra.getLogger()?.error("Error calling handler", {
					error: error instanceof Error ? {
						message: error.message,
						stack: error.stack
					} : error,
					path: route.path,
					method: route.method
				});
				const customResponse = getCustomHTTPExceptionResponse(error);
				if (customResponse) return customResponse;
				if (error && typeof error === "object") {
					if ("status" in error) {
						const status = error.status;
						let safeCause;
						try {
							const raw = error instanceof Error ? error.cause : void 0;
							if (raw && typeof raw === "object" && !Array.isArray(raw) && "failingItems" in raw && Array.isArray(raw.failingItems)) safeCause = { failingItems: raw.failingItems };
						} catch {}
						return c.json({
							error: error instanceof Error ? error.message : "Unknown error",
							...safeCause ? { cause: safeCause } : {}
						}, status);
					}
					if ("details" in error && error.details && typeof error.details === "object" && "status" in error.details) {
						const status = error.details.status;
						return c.json({ error: error instanceof Error ? error.message : "Unknown error" }, status);
					}
				}
				return c.json({ error: error instanceof Error ? error.message : "Unknown error" }, 500);
			}
		});
	}
	async registerCustomApiRoutes() {
		const routes = await this.registerSchemaApiRoutes();
		if (!await this.buildCustomRouteHandler(routes)) return;
		for (const route of routes) {
			const serverRoute = {
				method: route.method,
				path: route.path,
				responseType: "json",
				handler: async () => {},
				requiresAuth: route.requiresAuth,
				requiresPermission: route.requiresPermission,
				fga: route.fga
			};
			const routeHandler = async (c) => {
				const authError = await this.checkRouteAuth(serverRoute, {
					path: c.req.path,
					method: c.req.method,
					getHeader: (name) => c.req.header(name),
					getQuery: (name) => c.req.query(name),
					requestContext: c.get("requestContext"),
					request: c.req.raw,
					buildAuthorizeContext: () => c
				});
				if (authError) {
					if (authError.headers) for (const [key, value] of Object.entries(authError.headers)) c.header(key, value);
					if (authError.error) return c.json({ error: authError.error }, authError.status);
				}
				const requestContext = c.get("requestContext");
				if (this.mastra.getStudio?.()?.auth || this.mastra.getServer()?.auth) {
					const hasPermission = await loadHasPermission();
					if (hasPermission) {
						const userPermissions = requestContext.get("mastra__userPermissions");
						const permissionError = this.checkRoutePermission(serverRoute, userPermissions, hasPermission, requestContext);
						if (permissionError) return c.json({
							error: permissionError.error,
							message: permissionError.message
						}, permissionError.status);
					}
				}
				const pristineRequest = c.get(MASTRA_PRISTINE_REQUEST_KEY) ?? c.req.raw;
				let bodyParams = {};
				const contentType = c.req.header("content-type");
				if (contentType?.includes("application/json")) try {
					const body = await pristineRequest.clone().json();
					if (body && typeof body === "object" && !Array.isArray(body)) bodyParams = body;
				} catch {
					bodyParams = {};
				}
				else if (contentType?.includes("application/x-www-form-urlencoded") || contentType?.includes("multipart/form-data")) try {
					bodyParams = Object.fromEntries(await pristineRequest.clone().formData());
				} catch {
					bodyParams = {};
				}
				const fgaError = await checkRouteFGA(this.mastra, serverRoute, c.get("requestContext"), {
					...c.req.param(),
					...Object.fromEntries(new URL(c.req.url).searchParams.entries()),
					...bodyParams
				});
				if (fgaError) return c.json({
					error: fgaError.error,
					message: fgaError.message
				}, fgaError.status);
				const reqHeaders = {};
				c.req.raw.headers.forEach((v, k) => {
					reqHeaders[k] = v;
				});
				let executionCtx;
				try {
					executionCtx = c.executionCtx;
				} catch {
					executionCtx = void 0;
				}
				const response = await this.handleCustomRouteRequest(c.req.url, c.req.method, reqHeaders, pristineRequest.body, c.get("requestContext"), c.req.raw.signal, executionCtx);
				if (!response) return c.json({ error: "Not Found" }, 404);
				return response;
			};
			const method = route.method.toLowerCase();
			this.app[method](route.path, routeHandler);
		}
	}
	registerContextMiddleware() {
		const isFrameworkPublic = this.getFrameworkPublicMatcher();
		this.app.use("*", this.createContextMiddleware());
		this.app.use("*", async (c, next) => {
			c.set(MASTRA_FRAMEWORK_PUBLIC_KEY$1, isFrameworkPublic(c.req.path, c.req.method));
			return next();
		});
		this.app.use("*", async (c, next) => {
			await next();
			this.warnIfUnregisteredChannelWebhook(c.req.path, c.req.method, c.res.status);
		});
	}
	registerAuthMiddleware() {}
	registerUserMiddleware() {
		for (const m of this.mastra.getServerMiddleware?.() ?? []) this.app.use(m.path, skipIfFrameworkPublic(m.handler));
		const configMiddleware = this.mastra.getServer()?.middleware;
		if (!configMiddleware) return;
		const normalizedMiddlewares = Array.isArray(configMiddleware) ? configMiddleware : [configMiddleware];
		for (const middleware of normalizedMiddlewares) {
			const { path, handler } = typeof middleware === "function" ? {
				path: "*",
				handler: middleware
			} : middleware;
			this.app.use(path, skipIfFrameworkPublic(handler));
		}
	}
	registerHttpLoggingMiddleware() {
		if (!this.httpLoggingConfig?.enabled) return;
		this.app.use("*", async (c, next) => {
			if (!this.shouldLogRequest(c.req.path)) return next();
			const start = Date.now();
			const method = c.req.method;
			const path = c.req.path;
			await next();
			const duration = Date.now() - start;
			const status = c.res.status;
			const level = this.httpLoggingConfig?.level || "info";
			const logData = {
				method,
				path,
				status,
				duration: `${duration}ms`
			};
			if (this.httpLoggingConfig?.includeQueryParams) logData.query = c.req.query();
			if (this.httpLoggingConfig?.includeHeaders) {
				const headers = Object.fromEntries(c.req.raw.headers.entries());
				(this.httpLoggingConfig.redactHeaders || []).forEach((h) => {
					const key = h.toLowerCase();
					if (headers[key] !== void 0) headers[key] = "[REDACTED]";
				});
				logData.headers = headers;
			}
			this.logger[level](`${method} ${path} ${status} ${duration}ms`, logData);
		});
	}
};
//#endregion
export { MASTRA_FRAMEWORK_PUBLIC_KEY, MastraServer, createAuthMiddleware, setupBrowserStream, skipIfFrameworkPublic };

//# sourceMappingURL=index.js.map